import React, { useState, useEffect, useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  params, string, shape, number, extend, useDataModel, arrayOf, useLazyDataModel, customType,
  QueryProvider
} from '@thd-nucleus/data-sources';
import { useStore } from '@thd-nucleus/experience-context';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import {
  Drawer, DrawerHeader, DrawerBody, DrawerFooter, Button, Alert, Link
} from '@one-thd/sui-atomic-components';
import { CreateAProjectContent } from '@thd-olt-component-react/project-forms';
import { MyStoreForm, DeliveryZipcodeForm } from '@thd-olt-component-react/project-localization';
import { AddToCoreContext, AddToCoreProvider } from './AddToCoreContext';
import { SCREENS } from './coreConstants';
import { triggerAnalytics, onLocalizationUpdate, getValidSku, isItemIntent } from './coreHelpers';
import {
  TRACK, ANALYTICS_SELECT_PROJECT, LAST_USED_PROJECT_KEY,
  ADD_ITEM_NEW_PROJECT, ADD_ITEMS_PROJECT_NEW_GROUP, ADD_ITEMS_PROJECT_EXISTING_GROUP,
  HEADER_TEXT
} from '../AddToConstants';
import { useProjects, useProjectDetails, projectsModel, projectDetailsModel } from '../hooks/useProjects';
import { SignIn } from '../common/addto-signin/SignIn';
import { SelectProject } from '../addto/project/SelectProject';
import { ZeroProjectState } from '../addto/project/ZeroProjectState';
import { CreateProjectPreview } from '../addto/project/CreateProjectPreview';
import { AddToLoading } from '../common/addto-loading/AddToloading';

// Screen title mapping (core project screens only)
const SCREEN_TITLES = {
  [SCREENS.PROJECT_SELECT_PROJECT]: 'Select Project',
  [SCREENS.PROJECT_CREATE_PROJECT]: 'Create Project',
  [SCREENS.PROJECT_ZERO_STATE]: 'Add Items to Project',
  [SCREENS.CREATE_PROJECT_PREVIEW]: 'Add Items to Project',
  [SCREENS.PROJECT_SUCCESS]: 'Added to Project',
  [SCREENS.MY_STORE]: 'My Store',
  [SCREENS.DELIVERY_ZIP]: 'Delivery ZIP Code',
};

export const AddToProjectContent = ({ products: productsProp }) => {
  const {
    storeId,
    screen,
    pushScreen,
    replaceScreen,
    handleClose,
    selectedProductDetails,
    setSelectedProductDetails,
    showSuccessMessage,
    setShowSuccessMessage,
    selectedGroup,
    setSelectedGroup,
  } = useContext(AddToCoreContext);

  const { svocID } = useThdCustomer() || {};
  const isProjectCreated = useRef(false);
  const userSelectedProject = useRef(false);

  const [products, setProducts] = useState(productsProp);
  const [selectedProject, setSelectedProject] = useState({ projectId: '', projectName: '' });
  const [loadedProductsData, setLoadedProductsData] = useState([]);
  // Tracks whether the addItem mutation is in-flight. Used alongside projectLoading
  // to keep the loading spinner visible for the entire duration between project selection
  // and success/failure (projectLoading covers the details fetch, isAddingItem covers the mutation).
  const [isAddingItem, setIsAddingItem] = useState(false);
  const isMultiAdd = products?.length > 1
    || products?.some((item) => (!!item?.purchaseIntent && item?.itemType === 'intent'));
  const itemId = selectedProductDetails?.itemId || products[0]?.itemId;

  const { data } = useDataModel('product', {
    variables: { itemId, storeId },
    skip: isMultiAdd || !products[0]?.itemId
  });

  // Fetch product data for multi-product SKU enrichment
  const [fetchProductData] = useLazyDataModel('product', {
    variables: { itemId: '', storeId },
    fetchPolicy: 'cache-first',
  });

  // Enrich multi-product array with SKU data from fetched product details
  useEffect(() => {
    if (!isMultiAdd || productsProp?.length <= 1) {
      setProducts(productsProp);
      return;
    }

    const enrichProducts = async () => {
      const enrichedProducts = await Promise.all(
        productsProp.map(async (product) => {
          if (isItemIntent(product)) return product;

          // If product already has SKU data, use it
          const existingSku = getValidSku(product?.sku, product?.identifiers);
          if (existingSku) return { ...product, sku: existingSku };

          // Fetch product details to get SKU
          try {
            const response = await fetchProductData({
              variables: { itemId: product.itemId, storeId }
            });
            const fetchedIdentifiers = response?.data?.product?.identifiers;
            const sku = getValidSku(null, fetchedIdentifiers);
            return { ...product, sku: sku || null, identifiers: fetchedIdentifiers };
          } catch (_err) {
            return { ...product, sku: null };
          }
        })
      );
      setProducts(enrichedProducts);
      setLoadedProductsData(enrichedProducts);
    };

    enrichProducts();
  }, [productsProp, storeId]);

  const pageSize = 25;
  const [page, setPage] = useState(1);
  const [loadedProjects, setLoadedProjects] = useState([]);
  const [hasLoadedProjects, setHasLoadedProjects] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);

  const { product: singleProductData = {} } = data || selectedProductDetails?.productData || {};
  const {
    storeSkuNumber, omsThdSku, specialOrderSku
  } = singleProductData?.identifiers
    || selectedProductDetails?.productData?.identifiers
    || selectedProductDetails?.identifiers
    || {};
  const singleItemSku = storeSkuNumber || omsThdSku || specialOrderSku;
  const { pricing } = singleProductData;
  const basePrice = pricing?.value;
  const quantity = selectedProductDetails?.quantity || products[0]?.quantity;

  const { projectData, projectLoading, projectRefetch } = useProjectDetails({
    projectId: selectedProject.projectId,
    fetchPolicy: 'no-cache'
  });
  const {
    projectName = '',
    propertyType,
    addresses,
    defaultListId,
    defaultListMutationKey,
    assortmentZip,
    preferredStore,
  } = projectData?.projectDetails || {};
  const assortmentZipId = addresses?.find((addr) => addr.addressType === 'ASSORTMENT_ZIP')?.id || null;

  const {
    PROJECT_SELECT_PROJECT, PROJECT_CREATE_PROJECT, PROJECT_ZERO_STATE,
    CREATE_PROJECT_PREVIEW, LOADING, MY_STORE, DELIVERY_ZIP, SIGN_IN
  } = SCREENS;

  const singleProduct = [{ itemId, quantity, sku: singleItemSku, itemType: 'catalog' }];
  const multipleProduct = products.map((prod) => {
    if (isItemIntent(prod)) {
      const { groupName, comments, ...intentItem } = prod;
      return intentItem;
    }
    return {
      itemId: prod.itemId,
      quantity: prod.quantity,
      sku: getValidSku(prod.sku, prod.identifiers) || null,
      itemType: 'catalog'
    };
  }).filter((prod) => isItemIntent(prod) || prod.sku);

  const [addItem] = useLazyDataModel('updateMaterials', {
    variables: {
      listId: defaultListId,
      mutationKey: defaultListMutationKey,
      operation: 'ADD_ITEM',
      groupName: selectedGroup,
      items: isMultiAdd ? multipleProduct : singleProduct
    },
    context: { withAuth: true },
    fetchPolicy: 'no-cache'
  });

  const { projects, loading, refetch } = useProjects({
    page,
    pageSize,
    accountId: svocID,
    filter: [],
    sort: []
  });

  const projectsCount = projects?.projects?.totalProjects || 0;
  const isSelectionParentScreen = screen === SCREENS.ADD_TO_SELECTION;
  const isEntryLoadingScreen = screen === LOADING;

  useEffect(() => { refetch(); }, [svocID, page]);

  useEffect(() => {
    if (!loading) {
      setLoadedProjects((prev) => [...prev, ...(projects?.projects?.projects || [])]);
      setHasLoadedProjects(true);
    }
  }, [projects]);

  // Determine initial screen
  useEffect(() => {
    if (!initialLoad) return;
    const isLoading = loading || !hasLoadedProjects;
    const projectsExist = Boolean(loadedProjects?.length);
    let nextScreen = null;

    if (isLoading && !projectsExist) {
      nextScreen = LOADING;
    } else if (projectsExist) {
      nextScreen = PROJECT_SELECT_PROJECT;
      setInitialLoad(false);
      triggerAnalytics(ANALYTICS_SELECT_PROJECT, {});
    } else if (!isLoading && !projectsExist) {
      const hasProducts = products && products.length > 0;
      nextScreen = hasProducts ? PROJECT_ZERO_STATE : PROJECT_CREATE_PROJECT;
      setInitialLoad(false);
      if (hasProducts) {
        triggerAnalytics(TRACK.NO_PROJECTS_OVERLAY);
      }
    }

    if (!nextScreen) return;

    if (isEntryLoadingScreen && nextScreen !== LOADING) {
      replaceScreen(nextScreen);
    } else if (!screen || isSelectionParentScreen || screen !== nextScreen) {
      pushScreen(nextScreen);
    }
  }, [
    hasLoadedProjects,
    initialLoad,
    isEntryLoadingScreen,
    isSelectionParentScreen,
    loadedProjects,
    loading,
    products,
    pushScreen,
    replaceScreen,
    screen,
  ]);

  // Auto-add items once the selected project's details have fully loaded.
  // Flow: user selects project -> projectRefetch() fires -> projectLoading: true -> false.
  // We gate on `defaultListId` to ensure project details (which provide the list/mutation key)
  // are available before calling the mutation.
  useEffect(() => {
    if (screen === PROJECT_ZERO_STATE) return;
    if (products?.length > 0 && selectedProject?.projectId && !projectLoading
      && userSelectedProject.current && defaultListId) {
      addItemToProject({ groupName: 'Unassigned' });
    }
  }, [projectLoading, selectedProject, defaultListId]);

  useEffect(() => {
    if (selectedProject?.projectId) {
      projectRefetch();
    }
  }, [selectedProject?.projectId]);

  const fireAddItemAnalytics = ({ type, items }) => {
    const eventName = type === 'NEW_GROUP' ? ADD_ITEMS_PROJECT_NEW_GROUP : ADD_ITEMS_PROJECT_EXISTING_GROUP;
    LIFE_CYCLE_EVENT_BUS.trigger(eventName, {
      id: selectedProject?.projectId,
      type: propertyType ? 'y' : 'n',
      desc: projectName ? 'y' : 'n',
      address: addresses?.[0]?.addressLine1 ? 'y' : 'n',
      zip: addresses?.[0]?.zipCode ? 'y' : 'n',
      items
    });
  };

  const fireNewProjectAnalytics = () => {
    const items = isMultiAdd ? multipleProduct : singleProduct;
    LIFE_CYCLE_EVENT_BUS.trigger(ADD_ITEM_NEW_PROJECT, {
      id: selectedProject?.projectId,
      type: propertyType ? 'y' : 'n',
      desc: projectName ? 'y' : 'n',
      address: addresses?.[0]?.addressLine1 ? 'y' : 'n',
      zip: addresses?.[0]?.zipCode ? 'y' : 'n',
      items
    });
  };

  const addItemToProject = async ({ type = 'OLD_GROUP', groupName }) => {
    const mergedGroupName = groupName || selectedGroup;
    const response = await addItem({
      variables: { groupName: mergedGroupName }
    });
    setIsAddingItem(false);
    if (response?.data?.updateMaterials?.groupId) {
      try {
        localStorage.setItem(LAST_USED_PROJECT_KEY, JSON.stringify({
          projectId: selectedProject.projectId,
          projectName: selectedProject.projectName
        }));
      } catch (_error) { /* graceful fail in incognito */ }
      pushScreen(SCREENS.PROJECT_SUCCESS);
    }
    if (isProjectCreated.current === true) {
      fireNewProjectAnalytics();
    } else {
      const items = isMultiAdd ? multipleProduct : singleProduct;
      fireAddItemAnalytics({ type, items });
    }
    return response;
  };

  const handleProjectCreation = (response) => {
    if (response?.id) {
      userSelectedProject.current = true;
      setSelectedProject({ projectId: response.id, projectName: response.projectName });
    }
    isProjectCreated.current = true;
  };

  const handleZeroStateSuccess = () => {
    pushScreen(SCREENS.PROJECT_SUCCESS);
  };

  const handleInlineCreationSuccess = () => {
    pushScreen(SCREENS.PROJECT_SUCCESS);
  };

  // Show loading overlay while the full add-to-project sequence is in progress:
  // 1. projectLoading = project details refetch is in-flight
  // 2. isAddingItem = the addItem mutation is in-flight
  // This prevents SelectProject from flashing back between these two async phases.
  const showProjectSubmitLoading = screen === PROJECT_SELECT_PROJECT
    && userSelectedProject.current
    && (projectLoading || isAddingItem);

  return (
    <>
      {screen === SIGN_IN && <SignIn onClose={handleClose} />}
      {screen === LOADING && <AddToLoading />}
      {showProjectSubmitLoading && <AddToLoading />}
      {screen === PROJECT_CREATE_PROJECT && (
        <CreateAProjectContent onCreate={handleProjectCreation} handleClose={handleClose} isAddToDrawer />
      )}
      {screen === PROJECT_ZERO_STATE && (
        <ZeroProjectState
          products={products}
          onProjectCreatedAndItemAdded={handleZeroStateSuccess}
          singleProduct={singleProduct}
          multipleProduct={multipleProduct}
          basePrice={basePrice}
        />
      )}
      {screen === CREATE_PROJECT_PREVIEW && (
        <QueryProvider cacheKey="create-project-preview" dataSource="catalog">
          <CreateProjectPreview
            products={products}
            singleProduct={singleProduct}
            multipleProduct={multipleProduct}
            onProjectCreatedAndItemAdded={handleZeroStateSuccess}
            copyLocation=""
          />
        </QueryProvider>
      )}
      {screen === PROJECT_SELECT_PROJECT && !showProjectSubmitLoading && (
        <SelectProject
          products={products}
          singleProduct={singleProduct}
          multipleProduct={multipleProduct}
          projects={loadedProjects}
          projectsExist={!!loadedProjects?.length}
          setSelectedProject={(proj) => {
            userSelectedProject.current = true;
            // Set isAddingItem immediately so the loading state is continuous
            // from project selection through details fetch through mutation.
            setIsAddingItem(true);
            setSelectedProject(proj);
          }}
          pageSize={pageSize}
          projectsCount={projectsCount}
          loading={loading}
          setInitialLoad={setInitialLoad}
          setPage={setPage}
          page={page}
          onInlineCreationSuccess={handleInlineCreationSuccess}
        />
      )}
      {screen === MY_STORE && (
        <MyStoreForm
          projectId={selectedProject.projectId}
          projectName={projectName}
          assortmentZip={assortmentZip}
          storeId={preferredStore}
          onUpdate={onLocalizationUpdate}
        />
      )}
      {screen === DELIVERY_ZIP && (
        <DeliveryZipcodeForm
          projectId={selectedProject.projectId}
          projectName={projectName}
          assortmentZipCode={assortmentZip}
          assortmentZipId={assortmentZipId}
          preferredStore={preferredStore}
          onUpdate={onLocalizationUpdate}
        />
      )}
      {screen === SCREENS.PROJECT_SUCCESS && (
        <>
          <DrawerBody>
            <Alert status="success" data-testid="add-to-project-success">
              {isMultiAdd ? 'Items' : 'Item'} successfully added to&nbsp;
              <Link href={`/projects/${selectedProject.projectId}`} className="sui-text-sm sui-underline">
                {selectedProject.projectName || 'project'}
              </Link>
            </Alert>
          </DrawerBody>
          <DrawerFooter>
            <Button
              fullWidth
              variant="primary"
              onClick={handleClose}
              data-testid="add-to-project-success-ok"
            >
              OK
            </Button>
          </DrawerFooter>
        </>
      )}
    </>
  );
};

AddToProjectContent.propTypes = {
  products: PropTypes.arrayOf(PropTypes.shape({})),
};

AddToProjectContent.defaultProps = {
  products: [],
};

const AddToProjectDrawerHeader = ({ onClose }) => {
  const { screen, popScreen, canGoBack } = useContext(AddToCoreContext);

  const showBack = canGoBack
    && screen !== SCREENS.PROJECT_SELECT_PROJECT
    && screen !== SCREENS.PROJECT_ZERO_STATE
    && screen !== SCREENS.PROJECT_SUCCESS;

  const handleBackPress = () => {
    triggerAnalytics(TRACK.ADD_TO_OVERLAY_EXIT, { screen });
    popScreen();
  };

  return (
    <DrawerHeader
      data-testid="core-add-to-project-header"
      title={SCREEN_TITLES[screen] || 'Add to Project'}
      onClose={onClose}
      onBack={showBack ? handleBackPress : null}
    />
  );
};

AddToProjectDrawerHeader.propTypes = {
  onClose: PropTypes.func.isRequired,
};

export const AddToProject = (props) => {
  const {
    products,
    renderButton,
    defaultOpen,
    storeId: storeIdProp
  } = props;

  const [open, setOpen] = useState(defaultOpen || false);
  const [selectedProductDetails, setSelectedProductDetails] = useState({});
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);

  const store = useStore();
  const storeId = storeIdProp || store?.storeId;
  const { isLoggedIn } = useThdCustomer() || {};

  const handleOpen = () => {
    setShowSuccessMessage(false);
    setShowErrorMessage(false);
    setSelectedGroup(null);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setShowSuccessMessage(false);
    setShowErrorMessage(false);
  };

  const coreValue = {
    addToType: 'Project',
    products,
    storeId,
    onClose: handleClose,
    selectedProductDetails,
    setSelectedProductDetails,
    showSuccessMessage,
    setShowSuccessMessage,
    showErrorMessage,
    setShowErrorMessage,
    selectedGroup,
    setSelectedGroup,
    listRefetch: () => {},
    projectRefetch: () => {},
  };

  const renderTriggerButton = () => {
    if (renderButton) {
      return renderButton({ onClick: handleOpen });
    }
    return (
      <Button
        fullWidth
        variant="secondary"
        onClick={handleOpen}
        disabled={!products?.length}
        aria-label="Add to Project"
        data-testid="core-add-to-project-button"
      >
        Add to Project
      </Button>
    );
  };

  return (
    <>
      {renderTriggerButton()}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <Drawer open={open} onClose={handleClose} onClick={(e) => e.stopPropagation()}>
        <AddToCoreProvider value={coreValue}>
          <AddToProjectDrawerHeader onClose={handleClose} />
          <AddToProjectContent products={products} />
        </AddToCoreProvider>
      </Drawer>
    </>
  );
};

AddToProject.displayName = 'AddToProject';

AddToProject.propTypes = {
  products: PropTypes.arrayOf(PropTypes.shape({
    itemId: PropTypes.string,
    quantity: PropTypes.number,
    sku: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
  })),
  renderButton: PropTypes.func,
  defaultOpen: PropTypes.bool,
  storeId: PropTypes.string,
};

AddToProject.defaultProps = {
  products: [],
  renderButton: null,
  defaultOpen: false,
  storeId: null,
};

AddToProject.dataModel = extend(
  {
    product: params({ itemId: string().isRequired(), dataSource: string() }).shape({
      itemId: string(),
      identifiers: shape({
        storeSkuNumber: string(),
        omsThdSku: string(),
        specialOrderSku: string(),
      }),
      pricing: params({ storeId: string() }).shape({
        value: number({ float: true })
      }),
    })
  },
  {
    updateMaterials: params({
      listId: string().isRequired(),
      mutationKey: string().isRequired(),
      operation: customType('MaterialsOperationType').enum(
        ['ADD_ITEM', 'DELETE_ITEM', 'UPDATE_QUANTITY']
      ),
      groupName: string(),
      items: arrayOf(customType('Items').shape({
        itemId: string(),
        quantity: number(),
        sku: string(),
        itemType: string()
      }))
    }).mutation().shape({
      groupId: string()
    })
  },
  projectsModel,
  projectDetailsModel,
  CreateAProjectContent,
  DeliveryZipcodeForm,
  MyStoreForm,
  CreateProjectPreview,
  ZeroProjectState
);
